import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

type DigitizationStatus = Database["public"]["Enums"]["digitization_status"];

interface StatusUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentStatus: DigitizationStatus;
  requestId: number;
  onStatusUpdated: () => void;
}

export function StatusUpdateDialog({
  open,
  onOpenChange,
  currentStatus,
  requestId,
  onStatusUpdated,
}: StatusUpdateDialogProps) {
  const [status, setStatus] = useState<DigitizationStatus>(currentStatus);
  const [notes, setNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  const handleStatusUpdate = async () => {
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from("digitization_requests")
        .update({
          status,
          notes: notes ? notes : null,
          updated_at: new Date().toISOString(),
        })
        .eq("request_id", requestId);

      if (error) throw error;

      // If status is "completed", trigger a webhook event using the edge function
      if (status === "completed") {
        try {
          // Fetch the request data to include in the webhook payload
          // Now also include the creator's email in the query
          const { data: requestData } = await supabase
            .from("digitization_requests")
            .select(`
              *,
              creators (
                name,
                surname,
                nick_name,
                creator_id,
                email
              )
            `)
            .eq("request_id", requestId)
            .single();

          if (requestData) {
            // Call the trigger-webhook edge function
            const { error: webhookError } = await supabase.functions.invoke('trigger-webhook', {
              body: {
                event_code: "digitization.completed",
                event_data: {
                  request_id: requestId,
                  title: requestData.title,
                  creator: {
                    id: requestData.creators.creator_id,
                    name: requestData.creators.name,
                    surname: requestData.creators.surname,
                    nick_name: requestData.creators.nick_name,
                    email: requestData.creators.email // Include the email
                  },
                  completed_at: new Date().toISOString(),
                  notes: notes || null
                }
              }
            });

            if (webhookError) {
              console.error("Error triggering webhook:", webhookError);
              // We don't want to fail the status update if webhook triggering fails
            }
          }
        } catch (webhookError) {
          console.error("Error triggering webhook:", webhookError);
          // We don't want to fail the status update if webhook triggering fails
        }
      }

      toast({
        title: "Status Updated",
        description: `Request status has been updated to ${status}`,
      });

      onStatusUpdated();
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Request Status</DialogTitle>
          <DialogDescription>
            Change the status of this digitization request and add any relevant notes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as DigitizationStatus)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this status change..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button onClick={handleStatusUpdate} disabled={isUpdating}>
            {isUpdating ? "Updating..." : "Update Status"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
